import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faCaretRight } from "@fortawesome/pro-solid-svg-icons/faCaretRight";
import { faCaretUp } from "@fortawesome/pro-solid-svg-icons/faCaretUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Space from "antd/es/space";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { EntityType, EntityTypesSupportingChildLayouts } from "@mapmycustomers/shared/types/entity";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import RoundButton from "@app/component/RoundButton";
import { RootState } from "@app/store/rootReducer";

import {
  getActiveEntityType,
  getEditedLayout,
  getEditedLayoutDefaultCheckInVariant,
  isEditingChildLayout,
} from "../../store";
import { selectChildLayout } from "../../store/actions";
import DefaultCheckInVariant from "../../store/DefaultCheckInVariant";
import DefaultCheckInVariantTag from "../DefaultCheckInVariantTag";
import SetCheckinDefaultVariant from "../EditDefaultCheckInVariantAction";
import VariantItem from "../VariantItem";
import VariantPopover from "../VariantPopover";

import styles from "./VariantsList.module.scss";

const messages = defineMessages({
  [EntityType.ACTIVITY]: {
    id: "settings.formLayouts.modal.variants.popover.list.activity",
    defaultMessage: "Types that do not have a variant will defer to the primary layout",
    description: "Variants list description for activities layouts",
  },
  [EntityType.DEAL]: {
    id: "settings.formLayouts.modal.variants.popover.list.deals",
    defaultMessage: "Deals in a stage that do not have a variant will defer to the primary layout",
    description: "Variants list description text for deals layouts",
  },
  hideVariants: {
    id: "settings.formLayouts.modal.variants.popover.list.action.hide",
    defaultMessage: "Hide Variants",
    description: "Hide Variants tooltip text",
  },
  newVariant: {
    id: "settings.formLayouts.modal.variants.popover.newVariant",
    defaultMessage: "+ New Variant",
    description: "New Variant button title",
  },
  primary: {
    id: "settings.formLayouts.modal.variants.popover.list.primary",
    defaultMessage: "Primary",
    description: "Primary tag title",
  },
  showVariants: {
    id: "settings.formLayouts.modal.variants.popover.list.action.show",
    defaultMessage: "Show Variants",
    description: "Show Variants tooltip text",
  },
  text: {
    id: "settings.formLayouts.modal.variants.popover.text",
    defaultMessage: "Create Variant",
    description: "Create Variant popover title",
  },
  variants: {
    id: "settings.formLayouts.modal.variants.popover.list.variants",
    defaultMessage: "{num} {num, plural, one {Variant} other {Variants}}",
    description: "Variants number description text",
  },
});

interface Props {
  defaultCheckinVariant?: DefaultCheckInVariant;
  entityType: EntityTypesSupportingChildLayouts;
  isEditingChildLayout: boolean;
  layout?: FormLayout;
  selectChildLayout: typeof selectChildLayout;
}

const VariantsList: React.FC<Props> = ({
  defaultCheckinVariant,
  entityType,
  isEditingChildLayout,
  layout,
  selectChildLayout,
}) => {
  const intl = useIntl();

  const [expanded, , , toggleExpanded] = useBoolean(true);

  const handlePrimaryClick = useCallback(() => selectChildLayout(undefined), [selectChildLayout]);

  const childLayoutsCount = layout?.childLayouts?.length ?? 0;
  if (!Array.isArray(layout?.childLayouts) || !childLayoutsCount) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.information}>
        <Tooltip
          placement="topRight"
          title={
            expanded
              ? intl.formatMessage(messages.hideVariants)
              : intl.formatMessage(messages.showVariants)
          }
        >
          <RoundButton className={styles.expandBtn} onClick={toggleExpanded}>
            <FontAwesomeIcon icon={expanded ? faCaretUp : faCaretRight} />
          </RoundButton>
        </Tooltip>

        <Button
          className={cn(styles.btn, { [styles.active]: !isEditingChildLayout })}
          onClick={handlePrimaryClick}
        >
          <div className={styles.title}>
            <div className={styles.name}>{layout?.name}</div>

            <div className={styles.tags}>
              <Tag>{intl.formatMessage(messages.primary)}</Tag>
              {defaultCheckinVariant?.isPrimary && <DefaultCheckInVariantTag />}
            </div>
          </div>

          <div className={styles.description}>
            {expanded
              ? intl.formatMessage(messages[entityType])
              : intl.formatMessage(messages.variants, { num: childLayoutsCount })}
          </div>
        </Button>
      </div>

      {expanded && (
        <>
          <div className={styles.list}>
            {layout!.childLayouts!.map((childLayout) => (
              <div className={styles.item} key={childLayout.id}>
                <div className={styles.corner} />
                <div className={styles.content}>
                  <VariantItem childLayout={childLayout} />
                </div>
              </div>
            ))}
          </div>

          <div className={styles.new}>
            <Space>
              <VariantPopover>
                <Button className={styles.newBtn} type="link">
                  {intl.formatMessage(messages.newVariant)}
                </Button>
              </VariantPopover>
              <SetCheckinDefaultVariant />
            </Space>
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  defaultCheckinVariant: getEditedLayoutDefaultCheckInVariant(state),
  entityType: getActiveEntityType(state) as EntityTypesSupportingChildLayouts,
  isEditingChildLayout: isEditingChildLayout(state),
  layout: getEditedLayout(state),
});

const mapDispatchToProps = {
  selectChildLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(VariantsList);
